/**
 * Collects Excel ListObjects for AI dashboard planning — **only Raw_***:
 * includes a table if the **worksheet** name starts with `Raw_` or the **table** name starts with `Raw_`
 * (e.g. `Raw_TrialBalance` / `tbl_RawTrialBalance`). Rpt_*, _Config, etc. are excluded.
 *
 * Uses `workbook.tables` for complete enumeration. Sample rows + valueHints use **stratified**
 * rows across the table body (not only the first rows) so Year / Party / etc. hints reflect
 * the full dataset; a small subset of those rows is sent as sampleRows for token limits.
 *
 * valueHints: we ship distinct values for columns the model would realistically filter on —
 * the known-good finance/metadata names plus any auto-detected low-cardinality short-text column
 * (e.g. Department, Segment, RegionCode) so "anyone can type" prompts land on real filter text.
 */


import { getTableDataBodyRange, tableDataBodyHasRows } from './excelTableHelpers.js';

const DEFAULT_MAX_TABLES = 2000;

/** Real data for the model, but capped at five rows per table to keep prompts small. */
const DEFAULT_SAMPLE_ROWS = 8;

/** Stratified body rows read for valueHints (spread across rowCount — avoids "one Year" from top rows only). */
const DEFAULT_MAX_READ_ROWS = 40;

const MAX_COLUMN_NAMES = 80;

const MAX_DISTINCT_PER_COLUMN = 24;

/** Soft cap on total distinct strings across all valueHints per table (keeps the prompt small). */
const MAX_HINT_STRINGS_PER_TABLE = 180;

/** Keep hint strings short — the model only needs the filter value, not a full paragraph. */
const MAX_HINT_VALUE_LEN = 60;


function isRawSheetName(sheetName) {
  return /^raw_/i.test(String(sheetName || '').trim());
}

function isRawTableName(tableName) {
  return /^raw_/i.test(String(tableName || '').trim());
}

/** Trial balance first, then other Raw tables. */
function tableContextSortKey(worksheetName, tableName) {
  const blob = `${worksheetName}\t${tableName}`.toLowerCase();
  if (/trial|balance/.test(blob)) return 0;
  return 1;
}

/** Spread indices across [0, rowCount-1] so scans are not biased to the first rows (e.g. one Year). */
function stratifiedRowIndices(rowCount, maxCount) {
  if (rowCount <= 0) return [];
  const m = Math.min(maxCount, rowCount);
  if (m === rowCount) return Array.from({ length: rowCount }, (_, i) => i);
  const set = new Set();
  for (let k = 0; k < m; k++) {
    const idx = Math.round((k * (rowCount - 1)) / Math.max(1, m - 1));
    set.add(Math.max(0, Math.min(rowCount - 1, idx)));
  }
  let fill = 0;
  while (set.size < m && fill < rowCount) {
    if (!set.has(fill)) set.add(fill);
    fill++;
  }
  return [...set].sort((a, b) => a - b);
}

/** Fewer rows for the API payload, evenly picked from stratified hint rows. */
function subsampleRowsForPayload(rows, want) {
  if (!rows.length || rows.length <= want) return rows.slice();
  const out = [];
  for (let k = 0; k < want; k++) {
    const j = Math.round((k * (rows.length - 1)) / Math.max(1, want - 1));
    out.push(rows[j]);
  }
  return out;
}

/** Known-good columns that are ALWAYS worth hinting even if the auto-heuristic would skip them. */
const HINT_COLUMN_ALLOWLIST =
  /^(accountclass|account\s*class|statement|statementtype|section|incomesection|pnlsection|currency|currencycode|partyname|party|monthname|month|year|accountnumber|accountname|mapping|mastermapping|chartaccount|masteraccount|accounttype|normalbalance|category|equity|revenue|balancesheet|incomestatement)$/i;

/** Columns that LOOK like amounts/ids — hinting these is noise for the model (they are not filter targets). */
const HINT_COLUMN_DENYLIST =
  /^(id|[a-z]*id|amount|balance|balancechange|balancein|balanceout|debit|credit|net|total|qty|quantity|price|rate|exchangerate|fxrate|count|value|amt|sum)$/i;

function truncateColumns(names) {
  if (!names || names.length <= MAX_COLUMN_NAMES) return names || [];
  return names.slice(0, MAX_COLUMN_NAMES);
}

/**
 * A value is "hint-worthy" for a filter dropdown if it is short text (not a number/date/long prose).
 * We keep numeric-looking strings out because SUM columns should never be filter targets.
 */
function isHintWorthyValue(v) {
  if (v == null || v === '') return false;
  const s = String(v).trim();
  if (!s || s.length > MAX_HINT_VALUE_LEN) return false;
  // Skip pure numbers / decimals (these are measures, not filters).
  if (/^-?\d+([.,]\d+)?$/.test(s)) return false;
  return true;
}

/**
 * @param {string[]} columnNames
 * @param {any[][]} rows body rows aligned to columnNames
 * @returns {Record<string, string[]>}
 */
function buildValueHints(columnNames, rows) {
  const hints = {};
  if (!columnNames.length || !rows.length) return hints;

  /** Work per column: collect distinct short-text values across the stratified sample. */
  const perCol = columnNames.map((nameRaw) => {
    const name = String(nameRaw || '').trim();
    return {
      name,
      normalized: name.replace(/\s+/g, '').toLowerCase(),
      allowlisted: name && HINT_COLUMN_ALLOWLIST.test(name),
      denylisted: name && HINT_COLUMN_DENYLIST.test(name.replace(/\s+/g, '')),
      seen: new Set(),
    };
  });

  for (const row of rows) {
    for (let c = 0; c < perCol.length && c < row.length; c++) {
      const meta = perCol[c];
      if (!meta.name) continue;
      if (meta.denylisted && !meta.allowlisted) continue;
      if (meta.seen.size >= MAX_DISTINCT_PER_COLUMN) continue;
      const v = row[c];
      if (!isHintWorthyValue(v)) continue;
      meta.seen.add(String(v).trim());
    }
  }

  /** Include allowlisted columns unconditionally when non-empty; auto-detect others by cardinality. */
  let totalStrings = 0;
  for (const meta of perCol) {
    if (!meta.seen.size) continue;
    const distinct = [...meta.seen];
    const accept =
      meta.allowlisted ||
      /* Low-cardinality short-text column looks like a filter dimension. */
      distinct.length <= Math.min(MAX_DISTINCT_PER_COLUMN, Math.max(2, Math.ceil(rows.length / 2)));
    if (!accept) continue;
    if (totalStrings + distinct.length > MAX_HINT_STRINGS_PER_TABLE) {
      const budget = Math.max(0, MAX_HINT_STRINGS_PER_TABLE - totalStrings);
      if (budget === 0) break;
      hints[meta.name] = distinct.slice(0, budget);
      totalStrings += budget;
      break;
    }
    hints[meta.name] = distinct;
    totalStrings += distinct.length;
  }

  return hints;
}


/**
 * @param {object} [opts]
 * @param {number} [opts.maxTables] default 2000 (safety ceiling; set lower only for tests)
 * @param {number} [opts.sampleRows]
 * @param {number} [opts.maxReadRowsForSample]
 * @returns {Promise<{ workbookTables: object[], rawSheetsMissingExcelTable: string[], totalListObjectsInWorkbook: number }>}
 */
export async function collectWorkbookTablesForAi(opts = {}) {
  const maxTables = opts.maxTables ?? DEFAULT_MAX_TABLES;
  const sampleRows = opts.sampleRows ?? DEFAULT_SAMPLE_ROWS;
  const maxReadRows = opts.maxReadRowsForSample ?? DEFAULT_MAX_READ_ROWS;

  return Excel.run(async (context) => {
    const wb = context.workbook;
    /** Workbook-level collection — every ListObject (avoids missing tables when a sheet has multiple). */
    const tableColl = wb.tables;
    tableColl.load('items');
    await context.sync();

    const tblItems = tableColl.items;
    const listObjectCount = tblItems.length;

    const out = [];
    /** Raw_* worksheets that have no ListObject (plain ranges — amounts invisible until Insert→Table). */
    const rawSheetsMissingExcelTable = [];

    for (let i = 0; i < tblItems.length && out.length < maxTables; i++) {
      const t = tblItems[i];
      t.load(['name']);
      t.worksheet.load(['name']);
      await context.sync();

      const sheetName = t.worksheet.name;
      const name = t.name;
      if (!isRawSheetName(sheetName) && !isRawTableName(name)) {
        continue;
      }

      const headerRange = t.getHeaderRowRange();
      const bodyRange = getTableDataBodyRange(t);
      headerRange.load('values');
      bodyRange.load(['values', 'rowCount', 'columnCount']);
      await context.sync();

      const headerRow = headerRange.values && headerRange.values[0] ? headerRange.values[0] : [];
      const columnNames = truncateColumns(
        headerRow.map((c) => (c == null ? '' : String(c)).trim())
      );

      let rowCount = 0;
      let sample = [];
      let valueHints = {};
      if (tableDataBodyHasRows(bodyRange)) {
        rowCount = bodyRange.rowCount;
        const spreadN = Math.min(maxReadRows, rowCount);
        const hintIndices = stratifiedRowIndices(rowCount, spreadN);
        const colCount = bodyRange.columnCount;
        /* Table body ranges do not support getOffset(row,col,rows,cols); use getCell + getResizedRange (same as dashboardSpecExecutor chunks). */
        const rowRanges = hintIndices.map((idx) => {
          const topLeft = bodyRange.getCell(idx, 0);
          return topLeft.getResizedRange(0, Math.max(0, colCount - 1));
        });
        rowRanges.forEach((r) => r.load('values'));
        await context.sync();

        const hintRows = rowRanges.map((r) =>
          (r.values && r.values[0] ? r.values[0].map((c) => c) : [])
        );

        sample = subsampleRowsForPayload(hintRows, Math.min(sampleRows, hintRows.length));
        valueHints = buildValueHints(columnNames, hintRows);
      }

      out.push({
        worksheetName: sheetName,
        tableName: name,
        columnNames,
        rowCount,
        sampleRows: sample,
        ...(Object.keys(valueHints).length > 0 ? { valueHints } : {}),
      });
    }

    const sheets = wb.worksheets;
    sheets.load('items/name');
    await context.sync();
    const usedSheets = new Set(out.map((e) => e.worksheetName));
    for (const sh of sheets.items) {
      if (isRawSheetName(sh.name) && !usedSheets.has(sh.name)) {
        rawSheetsMissingExcelTable.push(sh.name);
      }
    }

    out.sort((a, b) => {
      const d = tableContextSortKey(a.worksheetName, a.tableName)
        - tableContextSortKey(b.worksheetName, b.tableName);
      if (d !== 0) return d;
      return `${a.worksheetName}\t${a.tableName}`.localeCompare(`${b.worksheetName}\t${b.tableName}`);
    });

    return {
      workbookTables: out,
      rawSheetsMissingExcelTable,
      totalListObjectsInWorkbook: listObjectCount,
    };
  });
}
